<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { fetchMarketplaceJson } from 'dashboard/helpers/marketplaceApi';
import TrendyolOrdersSection from './TrendyolOrdersSection.vue';
import TrendyolExpensesSection from './TrendyolExpensesSection.vue';
import TrendyolCashflowSection from './TrendyolCashflowSection.vue';

const route = useRoute();
const accountId = route.params.accountId;

const channel = ref('all');
const datePreset = ref('all'); // all | thismonth | lastmonth | 30g | 90g | custom
const DATE_PRESETS = [
  { v: 'all', l: 'Tüm 2026' },
  { v: 'thismonth', l: 'Bu Ay' },
  { v: 'lastmonth', l: 'Geçen Ay' },
  { v: '30g', l: 'Son 30G' },
  { v: '90g', l: 'Son 90G' },
  { v: 'custom', l: 'Özel' },
];
const customStart = ref('');
const customEnd = ref('');
const overview = ref({});
const projection = ref({});
const openSection = ref('orders'); // 'orders' | 'expenses' | 'projection' | null

const channelParam = () => {
  const p = new URLSearchParams();
  if (channel.value !== 'all') p.set('channel', channel.value);
  return p.toString();
};

const isoDay = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

// Seçili preset'e göre {start, end} (YYYY-MM-DD); 'all' → boş (tüm 2026).
const computeRange = () => {
  const today = new Date();
  switch (datePreset.value) {
    case 'custom':
      return { start: customStart.value, end: customEnd.value };
    case 'thismonth':
      return {
        start: isoDay(new Date(today.getFullYear(), today.getMonth(), 1)),
        end: isoDay(today),
      };
    case 'lastmonth':
      return {
        start: isoDay(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        end: isoDay(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case '30g':
    case '90g': {
      const s = new Date(today);
      s.setDate(s.getDate() - (datePreset.value === '30g' ? 30 : 90));
      return { start: isoDay(s), end: isoDay(today) };
    }
    default:
      return { start: '', end: '' };
  }
};

const overviewParams = () => {
  const p = new URLSearchParams();
  if (channel.value !== 'all') p.set('channel', channel.value);
  const { start, end } = computeRange();
  if (start) p.set('start', start);
  if (end) p.set('end', end);
  return p.toString();
};

const loadOverview = async () => {
  try {
    overview.value = await fetchMarketplaceJson(
      accountId,
      `trendyol_finances/overview?${overviewParams()}`
    );
  } catch (e) {
    overview.value = {};
  }
};

const loadProjection = async () => {
  try {
    projection.value = await fetchMarketplaceJson(
      accountId,
      `trendyol_finances/projection?${channelParam()}`
    );
  } catch (e) {
    projection.value = {};
  }
};

const refresh = () => {
  loadOverview();
  loadProjection();
};

watch(channel, refresh);
watch([datePreset, customStart, customEnd], loadOverview);
onMounted(refresh);

const moneyFmt = new Intl.NumberFormat('tr-TR', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const money = v => `${moneyFmt.format(Number(v) || 0)} ₺`;

const toggle = s => {
  openSection.value = openSection.value === s ? null : s;
};

const periodLabel = computed(() => {
  const { start, end } = computeRange();
  if (!start && !end) return '1 Ocak 2026 → bugün';
  const fmt = d =>
    d
      ? new Date(d).toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        })
      : '…';
  return `${fmt(start)} → ${fmt(end)}`;
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-auto bg-n-background">
    <div class="flex flex-col gap-4 p-6 mx-auto w-full max-w-[1500px]">
      <!-- Başlık + mağaza -->
      <div class="flex flex-wrap items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span class="i-lucide-wallet text-2xl text-n-slate-12" />
          <h1 class="text-2xl font-semibold text-n-slate-12">
            Trendyol Finans
          </h1>
        </div>
        <select
          v-model="channel"
          class="px-3 py-1.5 text-sm rounded-lg bg-n-solid-2 border border-n-weak text-n-slate-12"
        >
          <option value="all">Her İki Mağaza</option>
          <option value="trendyol_koza">Trendyol · Koza</option>
          <option value="trendyol_modax">Trendyol · ModaX</option>
        </select>
      </div>

      <!-- KATMAN 3: Gerçek Dönem Kârı özeti (en üstte sonuç) -->
      <div
        class="flex flex-col gap-3 p-5 rounded-2xl bg-n-solid-2 border border-n-weak"
      >
        <!-- Başlık + tatlı tarih seçici (segmented) -->
        <div class="flex flex-wrap items-center justify-between gap-2">
          <span
            class="text-xs font-medium tracking-wide text-n-slate-10 uppercase"
            >Gerçek Dönem Kârı · {{ periodLabel }}</span>
          <div
            class="flex items-center p-0.5 rounded-lg bg-n-alpha-black1 gap-0.5"
          >
            <button
              v-for="opt in DATE_PRESETS"
              :key="opt.v"
              class="px-2.5 py-1 text-xs font-medium rounded-md transition-colors"
              :class="
                datePreset === opt.v
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-10 hover:text-n-slate-12'
              "
              @click="datePreset = opt.v"
            >
              {{ opt.l }}
            </button>
          </div>
        </div>
        <!-- Özel aralık tarih girişleri -->
        <div
          v-if="datePreset === 'custom'"
          class="flex flex-wrap items-center gap-2 -mt-1"
        >
          <input
            v-model="customStart"
            type="date"
            class="px-2 py-1 text-xs rounded-md bg-n-solid-1 border border-n-weak text-n-slate-12"
          />
          <span class="text-n-slate-10 text-xs">→</span>
          <input
            v-model="customEnd"
            type="date"
            class="px-2 py-1 text-xs rounded-md bg-n-solid-1 border border-n-weak text-n-slate-12"
          />
        </div>
        <div class="flex flex-wrap items-center gap-x-4 gap-y-3">
          <!-- Sipariş Net -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Sipariş Net</span>
            <span class="text-xl font-semibold tabular-nums text-n-slate-12">{{
              money(overview.order_net)
            }}</span>
            <span class="text-[11px] text-n-slate-10">
              ödenen {{ money(overview.paid_net) }} · bekleyen
              {{ money(overview.pending_net) }}
            </span>
          </div>
          <span class="text-2xl font-light text-n-slate-9">−</span>
          <!-- Ek Giderler -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Ek Giderler</span>
            <span
              class="text-xl font-semibold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.additional_expenses) }}</span
            >
            <div class="flex flex-wrap gap-x-2 max-w-md">
              <span
                v-for="c in overview.expense_by_category || []"
                :key="c.category"
                class="text-[11px] text-n-slate-10"
              >
                {{ c.label }} {{ money(c.total) }}
              </span>
            </div>
          </div>
          <span class="text-2xl font-light text-n-slate-9">=</span>
          <!-- Gerçek Dönem (ara adım) -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Gerçek Dönem</span>
            <span class="text-xl font-semibold tabular-nums text-n-slate-12">{{
              money(overview.real_profit)
            }}</span>
            <span class="text-[11px] text-n-slate-10">ürün maliyeti hariç</span>
          </div>
          <span class="text-2xl font-light text-n-slate-9">−</span>
          <!-- Ürün Maliyeti (bilinen) -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Ürün Maliyeti</span>
            <span
              class="text-xl font-semibold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.product_cost) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >maliyet kapsama %{{
                (overview.cost_coverage || 0).toLocaleString('tr-TR')
              }}</span
            >
          </div>
          <span class="text-2xl font-light text-n-slate-9">−</span>
          <!-- Maliyetsiz Satış (kâr 0 sayıldı) -->
          <div class="flex flex-col">
            <span class="text-xs text-n-slate-11">Maliyetsiz Satış</span>
            <span
              class="text-xl font-semibold tabular-nums text-ruby-700 dark:text-ruby-400"
              >{{ money(overview.zeroed_sales) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >maliyeti bilinmeyen, kâr 0 sayıldı</span
            >
          </div>
          <span class="text-2xl font-light text-n-slate-9">=</span>
          <!-- Gerçek Kâr (sonuç) -->
          <div
            class="flex flex-col px-4 py-2 rounded-xl bg-green-50 dark:bg-green-900/20 ring-1 ring-green-400"
          >
            <span class="text-xs font-medium text-green-800 dark:text-green-300"
              >GERÇEK KÂR</span
            >
            <span
              class="text-2xl font-bold tabular-nums text-green-700 dark:text-green-400"
              >{{ money(overview.net_profit) }}</span
            >
            <span class="text-[11px] text-n-slate-10"
              >temkinli (en kötü ihtimal)</span
            >
          </div>
        </div>

        <!-- Maliyet kapsama uyarısı -->
        <p
          v-if="(overview.cost_coverage || 0) < 100"
          class="text-[11px] text-amber-700 dark:text-amber-400 p-2 rounded-lg bg-amber-50 dark:bg-amber-900/10 max-w-3xl"
        >
          ⚠️ Siparişlerin
          <strong>%{{
              (overview.cost_coverage || 0).toLocaleString('tr-TR')
            }}</strong>'inin ürün maliyeti tam biliniyor. Maliyeti bilinmeyen siparişler
          <strong>"maliyetine satıldı" (kâr 0)</strong> sayıldı — yani bu rakam
          <strong>en kötü ihtimal / temkinli</strong> gerçek kârdır. Eksik
          maliyetler girildikçe ve eski siparişlerin ürün detayı çekildikçe
          gerçek kâr <strong>YUKARI</strong> çıkacaktır.
        </p>

        <!-- Bekleyen Gerçek Tutar + açıklayıcı not -->
        <div class="flex flex-col gap-1 pt-3 border-t border-n-weak">
          <div class="flex flex-wrap items-baseline gap-2">
            <span class="text-sm text-n-slate-11"
              >Bekleyen Gerçek Tutar (yaklaşık):</span
            >
            <span
              class="text-lg font-semibold tabular-nums text-blue-700 dark:text-blue-400"
              >{{ money(overview.pending_real) }}</span
            >
          </div>
          <p class="text-[11px] text-n-slate-10 max-w-3xl">
            Gerçek dönem kârından şu ana kadar kasana giren (ödenen) tutarı
            düşünce kalan — yani kazandığın gerçek kârın
            <strong>henüz yolda olan</strong>, bundan sonra hesabına geçecek
            kısmı. <strong>Yaklaşıktır:</strong> ek giderlerin bir bölümü zaten
            geçmiş ödemelerden kesildiği için bu rakam gerçeğin biraz altında
            olabilir.
          </p>
        </div>
      </div>

      <!-- KATMAN 1: Sipariş Bazlı Finans (accordion) -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          class="flex items-center justify-between w-full px-5 py-3 bg-n-solid-2 hover:bg-n-alpha-black1"
          @click="toggle('orders')"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-list-ordered text-lg text-n-slate-11" />
            <span class="text-base font-semibold text-n-slate-12"
              >1 · Sipariş Bazlı Finans</span
            >
            <span class="text-xs text-n-slate-10"
              >{{ overview.order_count || 0 }} sipariş · gerçekleşen
              komisyon/kargo/net</span
            >
          </div>
          <span
            class="i-lucide-chevron-down text-xl text-n-slate-11 transition-transform"
            :class="openSection === 'orders' ? 'rotate-180' : ''"
          />
        </button>
        <div
          v-if="openSection === 'orders'"
          class="px-5 py-4 border-t border-n-weak"
        >
          <TrendyolOrdersSection />
        </div>
      </div>

      <!-- KATMAN 2: Dönem Giderleri (accordion) -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          class="flex items-center justify-between w-full px-5 py-3 bg-n-solid-2 hover:bg-n-alpha-black1"
          @click="toggle('expenses')"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-receipt-text text-lg text-n-slate-11" />
            <span class="text-base font-semibold text-n-slate-12"
              >2 · Dönem Giderleri</span
            >
            <span class="text-xs text-n-slate-10"
              >reklam/ceza/uluslararası/stopaj · sipariş net'inden
              düşülecek</span
            >
          </div>
          <span
            class="i-lucide-chevron-down text-xl text-n-slate-11 transition-transform"
            :class="openSection === 'expenses' ? 'rotate-180' : ''"
          />
        </button>
        <div
          v-if="openSection === 'expenses'"
          class="px-5 py-4 border-t border-n-weak"
        >
          <TrendyolExpensesSection />
        </div>
      </div>

      <!-- KATMAN 3: Beklenen / Tahmini (yolda olan siparişler) -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          class="flex items-center justify-between w-full px-5 py-3 bg-n-solid-2 hover:bg-n-alpha-black1"
          @click="toggle('projection')"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-hourglass text-lg text-n-slate-11" />
            <span class="text-base font-semibold text-n-slate-12"
              >3 · Beklenen (Tahmini)</span
            >
            <span class="text-xs text-n-slate-10"
              >{{ projection.count || 0 }} yolda sipariş · teslim olursa tahmini
              katkı {{ money(projection.est_real) }}</span
            >
          </div>
          <span
            class="i-lucide-chevron-down text-xl text-n-slate-11 transition-transform"
            :class="openSection === 'projection' ? 'rotate-180' : ''"
          />
        </button>
        <div
          v-if="openSection === 'projection'"
          class="flex flex-col gap-4 px-5 py-4 border-t border-n-weak"
        >
          <p class="text-sm text-n-slate-11">
            Henüz teslim edilmemiş (yolda/işlemde)
            <strong>{{ projection.count }} sipariş</strong> teslim edilirse
            diye,
            <strong>teslim edilenlerin son 90 günkü GERÇEK oranlarıyla</strong>
            hesaplanan tahmindir: komisyon %{{ projection.commission_rate }} ·
            ortalama kargo {{ money(projection.avg_cargo) }} · platform
            {{ money(projection.avg_platform) }} · dönem gideri oranı %{{
              projection.expense_rate
            }}.
          </p>

          <div class="flex flex-col gap-1 max-w-lg">
            <div class="flex justify-between py-1.5 border-b border-n-weak">
              <span class="text-n-slate-11">Tahmini Ciro</span>
              <span class="font-medium tabular-nums text-n-slate-12">{{
                money(projection.amount)
              }}</span>
            </div>
            <div class="flex justify-between py-1 text-sm">
              <span class="text-n-slate-10">− Tahmini Komisyon</span>
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-projection.est_commission)
              }}</span>
            </div>
            <div class="flex justify-between py-1 text-sm">
              <span class="text-n-slate-10">− Tahmini Kargo</span>
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-projection.est_cargo)
              }}</span>
            </div>
            <div
              class="flex justify-between py-1 text-sm border-b border-n-weak"
            >
              <span class="text-n-slate-10">− Tahmini Platform</span>
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-projection.est_platform)
              }}</span>
            </div>
            <div class="flex justify-between py-1.5">
              <span class="text-n-slate-11">= Tahmini Sipariş Net</span>
              <span class="font-medium tabular-nums text-n-slate-12">{{
                money(projection.est_net)
              }}</span>
            </div>
            <div
              class="flex justify-between py-1 text-sm border-b border-n-weak"
            >
              <span class="text-n-slate-10"
                >− Tahmini Dönem Gideri (oransal)</span
              >
              <span class="tabular-nums text-ruby-700 dark:text-ruby-400">{{
                money(-projection.est_expense)
              }}</span>
            </div>
            <div
              class="flex justify-between items-center px-3 py-2 mt-1 rounded-xl bg-green-50 dark:bg-green-900/20 ring-1 ring-green-400"
            >
              <span class="font-semibold text-green-800 dark:text-green-300"
                >Tahmini Gerçek Katkı</span
              >
              <span
                class="text-lg font-bold tabular-nums text-green-700 dark:text-green-400"
                >{{ money(projection.est_real) }}</span
              >
            </div>
          </div>

          <div
            class="flex flex-col gap-1 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 max-w-lg"
          >
            <div class="flex flex-wrap items-baseline gap-2">
              <span class="text-sm text-n-slate-11"
                >İade oranıyla gerçekçi beklenti:</span
              >
              <span
                class="text-base font-semibold tabular-nums text-amber-700 dark:text-amber-400"
                >{{ money(projection.realistic) }}</span
              >
            </div>
            <p class="text-[11px] text-n-slate-10">
              Geçmiş veriye göre yolda siparişlerin
              <strong>%{{ projection.return_rate }}'i iade</strong> dönebilir; o
              durumda iade kargosu + iadenin getirisizliği düşülünce gerçek
              katkı bu seviyeye iner. Tümü tahmindir, teslim oldukça gerçek
              rakam "Sipariş Bazlı Finans" katmanına geçer.
            </p>
          </div>
        </div>
      </div>

      <!-- KATMAN 4: Nakit Akışı / Ödeme Takvimi -->
      <div class="rounded-2xl border border-n-weak overflow-hidden">
        <button
          class="flex items-center justify-between w-full px-5 py-3 bg-n-solid-2 hover:bg-n-alpha-black1"
          @click="toggle('cashflow')"
        >
          <div class="flex items-center gap-2">
            <span class="i-lucide-banknote text-lg text-n-slate-11" />
            <span class="text-base font-semibold text-n-slate-12"
              >4 · Nakit Akışı / Ödeme Takvimi</span
            >
            <span class="text-xs text-n-slate-10"
              >para ne zaman, ne kadar hesabına girdi/girecek</span
            >
          </div>
          <span
            class="i-lucide-chevron-down text-xl text-n-slate-11 transition-transform"
            :class="openSection === 'cashflow' ? 'rotate-180' : ''"
          />
        </button>
        <div
          v-if="openSection === 'cashflow'"
          class="px-5 py-4 border-t border-n-weak"
        >
          <TrendyolCashflowSection :channel="channel" />
        </div>
      </div>
    </div>
  </div>
</template>
